import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { useMyBoards } from '@/entities/collab';
import { taskPath } from '@/entities/my-tasks';
import { useViewer } from '@/entities/viewer';
import { CalendarView } from '@/features/calendar';
import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { calendarBoardOf } from './calendar-board';
import styles from './GlobalCalendarPage.module.css';

/**
 * `/app/calendar`: the GLOBAL calendar - events and deadlines of every board the person can see
 * (personal boards, all their workspaces, boards shared with them alone), each board once. The
 * same calendar component as everywhere else; the server authorizes every board, so nothing here
 * can show a board the person has no access to.
 */
export function GlobalCalendarPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const viewer = useViewer();
  const boards = useMyBoards({ limit: 1000 });
  const user = viewer.status === 'authenticated' ? viewer.user : null;
  const list = useMemo(() => (boards.data ?? []).map(calendarBoardOf), [boards.data]);
  const workspaceNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const board of boards.data ?? []) {
      if (board.workspace_id && board.workspace_name)
        names.set(board.workspace_id, board.workspace_name);
    }
    return names;
  }, [boards.data]);

  if (boards.isPending) return <LoadingState />;
  if (boards.isError) {
    return <ErrorState error={boards.error} onRetry={() => void boards.refetch()} />;
  }
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/app" className={styles.back}>
          ← {t('calendar.global.back')}
        </Link>
        <h1 className={styles.title}>{t('calendar.global.pageTitle')}</h1>
        <span className={styles.workspace}>{t('calendar.global.subtitle')}</span>
      </header>
      {list.length === 0 ? (
        <EmptyState title={t('calendar.global.noBoards')} />
      ) : (
        <CalendarView
          scope={{ kind: 'all' }}
          boards={list}
          canEdit
          firstDay={user?.first_day_of_week ?? 0}
          timeZone={user?.timezone ?? null}
          locale={i18n.language}
          workspaceNames={workspaceNames}
          onOpenCard={(cardId, boardId) => void navigate(taskPath(boardId, cardId))}
        />
      )}
    </div>
  );
}
